import React, { useEffect, useState } from 'react';

// ICONS
import {
  BiologicIcon,
  HelperIcon,
  OtherIcon,
  OverTheCounterIcon,
  PhototherapyIcon,
  PrescriptionTopicalIcon,
  SystemicsIcon,
  Topical,
} from '../assets/icons';
import * as Unicons from '@iconscout/react-unicons';

// COMPONENTS
import Button from '../components/shared/button';
import { PRICING_SCALE_TOOLTIP } from '../utils/constants';
import Input from '../components/shared/forms/input';

// APOLLO
import { useMutation, useQuery } from 'react-apollo';
import { TreatmentOptionsAndMetadata } from '../libs/graphql/queries/data';
import { updateUserTreatments } from '../libs/graphql/mutations/user';
import { Me } from '../libs/graphql/queries/me';
import ReactTooltip from 'react-tooltip';

import MixpanelService, { MixpanelEvents } from '../libs/mixpanel';

const initialGroups = [
  {
    id: '2',
    name: 'Prescription Topical',
    alias: 'Topicals',
    icon: <PrescriptionTopicalIcon width={16} height={16} color={'#fff'} />,
    treatments: [],
    customTreatments: [],
    collapsed: false,
  },
  {
    id: '4',
    name: 'Phototherapy',
    alias: 'Phototherapy',
    icon: <PhototherapyIcon width={16} height={16} color={'#fff'} />,
    treatments: [],
    customTreatments: [],
    collapsed: false,
  },
  {
    id: '3',
    name: 'Systemics',
    alias: 'Systemics',
    icon: <SystemicsIcon width={16} height={16} color={'#fff'} />,
    treatments: [],
    customTreatments: [],
    collapsed: false,
  },
  {
    id: '5',
    name: 'Biologic',
    alias: 'Biologic',
    icon: <BiologicIcon width={16} height={16} color={'#fff'} />,
    treatments: [],
    customTreatments: [],
    collapsed: false,
  },
  {
    id: '0',
    name: 'Over the Counter',
    alias: 'OTC',
    icon: <OverTheCounterIcon width={16} height={16} color={'#fff'} />,
    treatments: [],
    customTreatments: [],
    collapsed: false,
  },
  {
    id: '6',
    name: 'Other',
    alias: 'Other',
    icon: <OtherIcon width={16} height={16} color={'#fff'} />,
    treatments: [],
    customTreatments: [],
    collapsed: false,
  },
  {
    id: '1',
    name: 'No Treatment',
    alias: 'No Treatment',
    icon: <OtherIcon width={16} height={16} color={'#fff'} />,
    treatments: [],
    customTreatments: [],
    collapsed: false,
  },
];

const TreatmentSettings = ({ me }) => {
  // Queries
  const { data: meData } = useQuery(Me, {
    fetchPolicy: 'network-only',
  });
  const { data: treatmentOptionsAndMetadata } = useQuery(
    TreatmentOptionsAndMetadata,
    {
      fetchPolicy: 'network-only',
    },
  );

  const [updateUserTreatmentsMutation] = useMutation(updateUserTreatments);

  // States
  const [customEditable, setCustomEditable] = useState(false);
  const [editable, setEditable] = useState(true);
  const [groups, setGroups] = useState(initialGroups);
  const [treatmentToShow, setTreatmentToShow] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredTreatments, setFilteredTreatments] = useState([]);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [newTreatmentName, setNewTreatmentName] = useState('');
  const [newTreatmentGroup, setNewTreatmentGroup] = useState('');

  const validateTreatmentOptions = (treatmentOptions, userTreatments) => {
    const newGroups = [...groups];

    newGroups.forEach((group, key) => {
      const groupOptions = treatmentOptions.filter(
        treatmentOption => treatmentOption.group_id === group.id,
      );

      const userTreatmentsForGroup = userTreatments.filter(
        treatment =>
          group.name.includes(treatment.type) ||
          group.alias.includes(treatment.type),
      );

      userTreatmentsForGroup.forEach(treatment => {
        const treatmentInGroup = groupOptions.find(
          treatmentOption => treatmentOption.name === treatment.name,
        );
        if (!treatmentInGroup) {
          groupOptions.push({
            id: treatment.id,
            name: treatment.name,
            group: group.name,
            active: treatment.active,
          });
        }
      });

      newGroups[key].treatments = [...groupOptions];
    });

    setGroups(newGroups);
  };

  useEffect(() => {
    if (treatmentOptionsAndMetadata?.availableTreatmentOptions) {
      validateTreatmentOptions(
        treatmentOptionsAndMetadata.availableTreatmentOptions,
        meData?.me?.profile?.userTreatments || [],
      );
    }
  }, [treatmentOptionsAndMetadata, meData]);

  useEffect(() => {
    if (selectedTreatments.length > 0) {
      return;
    }
    const userTreatments = meData?.me?.profile?.userTreatments || [];
    const newUserTreatments = userTreatments.map(treatment => ({
      ...treatment,
    }));
    setSelectedTreatments(newUserTreatments);
    setHasChanges(false);
  }, [meData]);

  useEffect(() => {
    sumarizeGroups(selectedTreatments);
  }, [selectedTreatments]);

  const sumarizeGroups = newSelectedTreatments => {
    let newGroups = groups.map(group => ({
      ...group,
      treatments: [...group.treatments], // assuming treatments is an array
    }));

    // Find custom treatments
    newSelectedTreatments.forEach(treatment => {
      const foundGroupIndex = newGroups.findIndex(
        group =>
          group.name.includes(treatment.type) ||
          group.alias.includes(treatment.type),
      );
      if (foundGroupIndex > -1) {
        const foundTreatmentIndex = newGroups[
          foundGroupIndex
        ].treatments.findIndex(t => t.name === treatment.name);

        if (foundTreatmentIndex < 0) {
          newGroups[foundGroupIndex].treatments = [
            ...newGroups[foundGroupIndex].treatments,
            {
              name: treatment.name,
              group: newGroups[foundGroupIndex].name,
              type: newGroups[foundGroupIndex].name,
            },
          ];
        }
      }
    });

    setGroups(newGroups);
  };

  const countSelectedItemsInGroup = group => {
    const treatmentNames = group.treatments.map(treatment => treatment.name);
    const activeTreatments = selectedTreatments.filter(
      treatment => treatmentNames.includes(treatment.name) && treatment.active,
    );
    return activeTreatments.length;
  };

  const onSearchChange = e => {
    const searchText = e.target.value;

    setSearchText(searchText);
    if (!searchText) {
      setFilteredTreatments([]);
      return;
    }

    const filtered =
      treatmentOptionsAndMetadata.availableTreatmentOptions.filter(
        treatment => {
          return treatment.name
            .toLowerCase()
            .includes(searchText.toLowerCase());
        },
      );
    const filteredTreatmentNames = filtered.map(treatment => treatment.name);

    const filteredInSelectedTreatments = selectedTreatments.filter(
      treatment =>
        !filteredTreatmentNames.includes(treatment.name) &&
        treatment.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    setTreatmentToShow(null);
    setFilteredTreatments([...filtered, ...filteredInSelectedTreatments]);
  };

  const clearSearch = () => {
    setSearchText('');
    setFilteredTreatments([]);
  };

  const onSelectTreatment = treatmentName => {
    if (treatmentName === treatmentToShow?.name) {
      setTreatmentToShow(null);
      return;
    }

    const treatment =
      treatmentOptionsAndMetadata.availableTreatmentOptions.find(
        t => t.name === treatmentName,
      );
    if (treatment) {
      const metadata =
        treatmentOptionsAndMetadata.availableTreatmentOptionsMetadata.find(
          m => m.id === treatment.metadata_id,
        );
      if (metadata) {
        treatment.metadata = metadata;
      }
      setTreatmentToShow(treatment);
    } else {
      setTreatmentToShow({
        id: 0,
        name: treatmentName,
      });
    }
  };

  const onToggleTreatment = treatment => {
    const newSelectedTreatments = [...selectedTreatments];
    const existingIndex = selectedTreatments.findIndex(
      t => t.name === treatment.name,
    );

    if (existingIndex > -1) {
      const newStatus = !newSelectedTreatments[existingIndex].active;
      newSelectedTreatments[existingIndex].active = newStatus;

      if (treatmentToShow?.name === treatment.name && !newStatus && !editable) {
        setTreatmentToShow(null);
      }
    } else {
      newSelectedTreatments.push({
        type: treatment.group,
        name: treatment.name,
        active: !treatment.active,
        id: 'temp',
      });
    }

    setSelectedTreatments(newSelectedTreatments);
    setHasChanges(true);
  };

  const getCostLabel = cost => {
    if (cost === 0 || cost === null) {
      return '0';
    }
    return Array(cost).fill('$').join('');
  };

  const onCloseTreatmentDetails = () => {
    setTreatmentToShow(null);
  };

  const onAddTreatmentPress = () => {
    setEditable(true);
  };

  const cancelCustomChanges = async () => {
    setCustomEditable(false);
  };

  const isTreatmentSelected = treatment => {
    return (
      selectedTreatments.findIndex(t => t.name === treatment.name && t.active) >
      -1
    );
  };

  const toggleGroupCollapse = groupId => {
    const newGroups = [...groups];
    newGroups.forEach((g, key) => {
      if (g.id === groupId) {
        newGroups[key].collapsed = !newGroups[key].collapsed;
      }
    });
    setGroups(newGroups);
  };

  const cancelChanges = async () => {
    const userTreatments = meData?.me?.profile?.userTreatments || [];
    const newUserTreatments = userTreatments.map(treatment => ({
      ...treatment,
    }));
    setSelectedTreatments(newUserTreatments);
    setHasChanges(false);
    setTreatmentToShow(null);
    setSearchText('');
  };

  const saveChanges = async () => {
    setEditable(false);

    const treatments = selectedTreatments.map(trt => {
      return {
        profileid: meData?.me?.profile?.id || 0,
        type: trt.type,
        name: trt.name,
        active: trt.active,
        id: trt.id || null,
      };
    });

    const response = await updateUserTreatmentsMutation({
      variables: {
        treatments: treatments,
      },
      refetchQueries: () => [
        {
          query: Me,
          fetchPolicy: 'network-only',
        },
      ],
    });

    // Check for added treatments
    const userTreatments = meData?.me?.profile?.userTreatments || [];

    if (response?.data?.editUserTreatments?.profile?.userTreatments) {
      setSelectedTreatments(
        response.data.editUserTreatments.profile.userTreatments,
      );
    }

    treatments.forEach(trt => {
      if (!trt.active) {
        return;
      }

      const originalTreatment = userTreatments.find(
        ut => ut.name === trt.name && ut.type === trt.type && ut.active,
      );

      if (!originalTreatment) {
        MixpanelService.track(MixpanelEvents.TreatmentAdded, {
          TreatmentTypeAdded: trt.type,
          TreatmentTypeAddedName: trt.name,
        });
      }
    });

    userTreatments.forEach(originalTrt => {
      if (!originalTrt.active) {
        return;
      }

      const stillExists = selectedTreatments.some(
        trt =>
          trt.name === originalTrt.name &&
          trt.type === originalTrt.type &&
          trt.active,
      );

      if (!stillExists) {
        MixpanelService.track(MixpanelEvents.TreatmentDropped, {
          TreatmentTypeAdded: originalTrt.type,
          TreatmentTypeAddedName: originalTrt.name,
        });
      }
    });

    setHasChanges(false);
    setSearchText('');
    setTreatmentToShow(null);
  };

  const onSaveNewTreatment = async () => {
    const existingTreatment = selectedTreatments.find(
      treatment =>
        treatment.name.toLowerCase() === newTreatmentName.toLowerCase(),
    );
    if (existingTreatment) {
      alert(`Treatment "${newTreatmentName}" already exists`);
      return;
    }

    const newSelectedTreatments = [...selectedTreatments];
    newSelectedTreatments.push({
      id: 'temp',
      type: newTreatmentGroup,
      group: newTreatmentGroup,
      name: newTreatmentName,
      active: true,
    });

    setNewTreatmentName('');
    setNewTreatmentGroup('');
    setSelectedTreatments(newSelectedTreatments);
    setCustomEditable(false);
    setHasChanges(true);
    setSearchText('');
  };

  const onAddNewTreatmentByName = name => {
    setNewTreatmentName(name);
    setNewTreatmentGroup('');
    setCustomEditable(true);
  };

  const onAddNewTreatmentByGroup = groupName => {
    setNewTreatmentGroup(groupName);
    setNewTreatmentName('');
    setCustomEditable(true);
  };

  const goBack = () => {
    window.history.back();
  };

  const renderCustomTreatmentForm = () => {
    return (
      <div className="main-dashboard page-treatment-settings">
        <header>
          <div className="date-bar date-bar__provider-prep">
            <div className="main-dashboard__sidebar">
              <div className="sidebar-title">
                <Unicons.UilArrowLeft
                  size="24"
                  className="header-icon"
                  onClick={cancelCustomChanges}
                />
                <h1 className="headers headers__black100 headers__uppercase headers__mediumbold">
                  Add new treatment
                </h1>
              </div>
            </div>
          </div>
        </header>
        <div className="custom-treatment-form">
          <div className="form-item">
            <label>
              Treatment name <span className="field__required">*</span>
            </label>
            <Input
              name="treatment_name"
              inputType="text"
              content={newTreatmentName}
              controlFunc={e => setNewTreatmentName(e.target.value)}
              placeholder="Add treatment name"
              maxLength={50}
              required
            />
          </div>
          <div className="form-item">
            <label>
              Select category <span className="field__required">*</span>
            </label>
            <select
              name="treatment_group"
              inputType="text"
              content={newTreatmentGroup}
              onChange={e => setNewTreatmentGroup(e.target.value)}
              placeholder="Add treatment name"
              required>
              <option value="" selected>
                Select category
              </option>
              <hr />
              {groups.map(group => {
                return (
                  <option
                    key={group.id}
                    value={group.name}
                    selected={group.name === newTreatmentGroup}>
                    {group.name}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="form-buttons">
            <Button
              className="button button__secondary100-inverted button__btn-14-600 button-rectangle-skin-footer"
              onClick={cancelCustomChanges}
              text="Cancel"
              type="button"
            />
            <Button
              onClick={onSaveNewTreatment}
              className="button button__secondary100 button__btn-14-600 button-rectangle-skin-footer"
              text="Save treatment"
              disabled={!newTreatmentName || !newTreatmentGroup}
              type="submit"
            />
          </div>
        </div>
      </div>
    );
  };

  if (customEditable) {
    return renderCustomTreatmentForm();
  }

  return (
    <div className="main-dashboard page-treatment-settings">
      <header>
        <div className="date-bar date-bar__provider-prep">
          <div className="main-dashboard__sidebar">
            <div className="sidebar-title">
              <Unicons.UilTimes
                size="24"
                className="header-icon"
                onClick={goBack}
              />
              <h1 className="headers headers__black100 headers__uppercase headers__mediumbold">
                Edit treatments
              </h1>
            </div>
          </div>
          <div className="header-buttons">
            {!editable && (
              <button
                className="button button__secondary100 button__btn-14-600 button-rectangle-medium buttonCancel"
                onClick={onAddTreatmentPress}>
                Edit treatments
              </button>
            )}
          </div>
        </div>
      </header>
      <div className="provider-prep-container">
        <div className="treatments">
          <ReactTooltip
            type="light"
            className={'help-tooltip'}
            multiline={true}
            place="top"
            html={true}
            clickable
            key={treatmentToShow?.name || 'tooltip'}
          />
          <div className="treatment-options">
            <div class="card card-treatments-header">
              <h3>
                {searchText ? `Search "${searchText}"` : 'All treatments'}
              </h3>
              {editable && (
                <div className="search-bar">
                  {searchText ? (
                    <label>
                      <Unicons.UilTimes
                        size="16"
                        className="search-icon"
                        onClick={clearSearch}
                      />
                    </label>
                  ) : (
                    <label htmlFor="treatmetsSearch">
                      <Unicons.UilSearch size="16" className="search-icon" />
                    </label>
                  )}
                  <input
                    type="text"
                    placeholder="Search treatments"
                    className="search-input"
                    id="treatmetsSearch"
                    value={searchText}
                    onChange={onSearchChange}
                  />
                </div>
              )}
            </div>

            <div className={`card collapsible active`}>
              {!searchText && (
                <div className="card-content card-content-treatments">
                  {groups.map(group => {
                    const Icon = group.icon;
                    const selectedItemsInGroup =
                      countSelectedItemsInGroup(group);
                    if (
                      group.treatments.length === 0 ||
                      (!editable && !selectedItemsInGroup)
                    ) {
                      return null;
                    }
                    return (
                      <div
                        className={`treatment-group ${
                          !group.collapsed ? 'active' : ''
                        }`}
                        key={group.id}>
                        <div
                          className="group-header"
                          onClick={() => toggleGroupCollapse(group.id)}>
                          <div className="group-header-title">
                            {Icon}
                            <h4>
                              {group.name}
                              {selectedItemsInGroup > 0 &&
                                ` (${selectedItemsInGroup})`}
                            </h4>
                          </div>
                          <div className="group-header-button">
                            <Unicons.UilAngleDown size="24" />
                          </div>
                        </div>
                        <div className="treatment-list">
                          {group.treatments.map(treatment => {
                            if (!editable && !isTreatmentSelected(treatment)) {
                              return null;
                            }
                            return (
                              <div
                                className={`treatment-item ${
                                  treatment.name === treatmentToShow?.name
                                    ? 'active'
                                    : ''
                                }`}
                                key={`${group.name}-${treatment.name}`}
                                onClick={() =>
                                  onSelectTreatment(treatment.name)
                                }>
                                <div className="treatment-item-title">
                                  <div className="question-button">
                                    <Unicons.UilQuestion size="16" />
                                  </div>
                                  <div className="treatment-item-title">
                                    <span>{treatment.name}</span>
                                  </div>
                                  {editable ? (
                                    <div
                                      className={`treatment-add-button ${
                                        isTreatmentSelected(treatment)
                                          ? 'active'
                                          : ''
                                      }`}
                                      onClick={e => {
                                        e.stopPropagation();
                                        onToggleTreatment(treatment);
                                      }}>
                                      {isTreatmentSelected(treatment) ? (
                                        <Unicons.UilCheck size="16" />
                                      ) : (
                                        <Unicons.UilPlus size="16" />
                                      )}
                                    </div>
                                  ) : (
                                    <div
                                      className="treatment-remove-button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        onToggleTreatment(treatment);
                                      }}>
                                      <Unicons.UilTimes size="16" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {editable && (
                            <div
                              className={`treatment-item`}
                              onClick={e => {
                                e.stopPropagation();
                                onAddNewTreatmentByGroup(group.name);
                              }}>
                              <div className="treatment-item-title add">
                                <span>Add new treatment</span>
                              </div>
                              <div className={`treatment-add-button add`}>
                                <Unicons.UilPlus size="16" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {searchText && (
                <div className="treatment-list">
                  {filteredTreatments.map(treatment => (
                    <div
                      className={`treatment-item ${
                        treatment.name === treatmentToShow?.name ? 'active' : ''
                      }`}
                      key={`${treatment.group || treatment.type}-${
                        treatment.name
                      }`}>
                      <div
                        className="treatment-item-title"
                        onClick={() => onSelectTreatment(treatment.name)}>
                        <div className="question-button">
                          <Unicons.UilQuestion size="16" />
                        </div>
                        <div className="treatment-item-title">
                          <span>{treatment.name}</span>
                          <span>-</span>
                          <span className="subtitle">
                            in {treatment.group || treatment.type}
                          </span>
                        </div>
                        {editable && (
                          <div
                            className={`treatment-add-button ${
                              isTreatmentSelected(treatment) ? 'active' : ''
                            }`}
                            onClick={e => {
                              e.stopPropagation();
                              onToggleTreatment(treatment);
                            }}>
                            {isTreatmentSelected(treatment) ? (
                              <Unicons.UilCheck size="16" />
                            ) : (
                              <Unicons.UilPlus size="16" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div
                    className={`treatment-item`}
                    onClick={e => {
                      e.stopPropagation();
                      onAddNewTreatmentByName(searchText);
                    }}>
                    <div className="treatment-item-title add">
                      <span>Add {`<${searchText}>`} as Treatment</span>
                    </div>
                    <div className={`treatment-add-button add`}>
                      <Unicons.UilPlus size="16" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={`treatment-details ${treatmentToShow ? 'open' : ''}`}>
            <div className="card">
              <div className="card-header card-header--bordered">
                <h3>Treatment details</h3>
                <Unicons.UilTimes
                  size="24"
                  color="#000"
                  onClick={onCloseTreatmentDetails}
                  className="close-button"
                />
              </div>
              {treatmentToShow?.name && (
                <div className="card-content treatment-info">
                  <label>CATEGORY</label>
                  <h4 className="treatment-title">{treatmentToShow.name}</h4>
                  {treatmentToShow.brand_names && (
                    <>
                      <hr />
                      <h4>Brand names</h4>
                      <p>{treatmentToShow.brand_names.join(', ')}</p>
                    </>
                  )}
                  {treatmentToShow.generic && (
                    <>
                      <hr />
                      <h4>Generic available</h4>
                    </>
                  )}
                  <hr />
                  {treatmentToShow?.metadata?.how && (
                    <div className="info-item">
                      <h4>How does it work?</h4>
                      <p>{treatmentToShow.metadata.how}</p>
                    </div>
                  )}
                  {treatmentToShow?.metadata?.who && (
                    <div className="info-item">
                      <h4>Who is this for?</h4>
                      <p>{treatmentToShow.metadata.who}</p>
                    </div>
                  )}
                  {treatmentToShow?.metadata?.use && (
                    <div className="info-item">
                      <h4>How do I use it?</h4>
                      <p>{treatmentToShow.metadata.use}</p>
                    </div>
                  )}
                  {treatmentToShow?.metadata?.use && (
                    <div className="info-item">
                      <h4>How well does it work?</h4>
                      <p>{treatmentToShow.metadata.efficacy}</p>
                    </div>
                  )}
                  {treatmentToShow?.metadata?.effects && (
                    <div className="info-item">
                      <h4>What are common side effects?</h4>
                      <p>{treatmentToShow.metadata.effects}</p>
                    </div>
                  )}
                  {treatmentToShow.metadata && (
                    <div className="info-item">
                      <div className="info-item-title">
                        <h4> How much does it cost?</h4>
                        <div
                          className="help-button"
                          data-tip={PRICING_SCALE_TOOLTIP}>
                          <HelperIcon />
                        </div>
                      </div>
                      <div className="price-badge">
                        {getCostLabel(treatmentToShow.metadata.cost)}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!treatmentToShow?.name && (
                <div className="card-content treatments-placeholder">
                  <div className="placeholder-icon">
                    <Topical fill="#fff" />
                  </div>
                  <h4>Select a treatment to view the details</h4>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {hasChanges && (
        <div className="footer-buttons">
          <Button
            className="button button__secondary100-inverted button__btn-14-600 button-rectangle-skin-footer"
            onClick={cancelChanges}
            text="Cancel"
          />
          <Button
            onClick={saveChanges}
            className="button button__secondary100 button__btn-14-600 button-rectangle-skin-footer"
            text="Save treatments"
            disabled={false}
          />
        </div>
      )}
    </div>
  );
};

export default TreatmentSettings;
